import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { renderCornerCard } from './cornerCard';
import { renderCouponWheel } from './couponWheel';
import { placeholderFor, renderInContent } from './inContent';
import { renderLightbox } from './lightbox';
import { renderRibbon } from './ribbon';
import { renderScreenFiller } from './screenFiller';
import type { RenderHandle } from './shared';
import { renderTwoStep } from './twoStep';
import { renderWidget } from './widget';

export { placeholderFor };

/** True when this form's display type will actually have somewhere to render on the current page.
 * IN_CONTENT is the one type that depends on page markup the SDK doesn't control (a
 * `[data-cms-lf]` placeholder the site owner adds) — index.ts checks this before even arming that
 * form's trigger, so an IN_CONTENT form with no placeholder on this page never fires an impression
 * for a form nobody could see. Every other display type always has somewhere to go. */
export function canRender(form: LeadFormPublicConfig): boolean {
  return form.displayType !== 'IN_CONTENT' || placeholderFor(form.slug) !== null;
}

export function renderByDisplayType(
  form: LeadFormPublicConfig,
  client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  switch (form.displayType) {
    case 'LIGHTBOX':
      return renderLightbox(form, client, onSubmit);
    case 'RIBBON':
      return renderRibbon(form, client, onSubmit);
    case 'SLIDE_IN':
      return renderCornerCard(form, client, onSubmit, 'slim');
    case 'SCROLL_BOX':
      return renderCornerCard(form, client, onSubmit, 'full');
    case 'SCREEN_FILLER':
      return renderScreenFiller(form, client, onSubmit);
    case 'IN_CONTENT':
      return renderInContent(form, client, onSubmit);
    case 'WIDGET':
      return renderWidget(form, client, onSubmit);
    case 'TWO_STEP':
      return renderTwoStep(form, client, onSubmit);
    case 'COUPON_WHEEL':
      return renderCouponWheel(form, client, onSubmit);
    default:
      return { close: () => {} };
  }
}
